import { ReactNode } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  Minus,
  MoreVertical,
  RotateCcw,
  SquarePen,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import {
  AgentAssociationDisplay,
  DocumentTypeIcon,
  FolderBadge,
} from "@/components/knowledge-base";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/auth/AuthProvider";
import { useDocumentParsingStatus } from "@/hooks/documents/useDocumentParsingStatus";
import { DocumentService } from "@/services/api/DocumentService";
import { PDFService } from "@/services/knowledgebase/PDFService";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface MyDocumentsSectionProps {
  isLoading: boolean;
  documents: DocumentWithMetadata[];
  openDocumentDetails: (doc: DocumentWithMetadata) => void;
  handleDeleteDocument: (id: string) => void;
  handleSendToElevenLabs?: (doc: DocumentWithMetadata) => void;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  headerRight?: ReactNode;
  deletingDocumentId?: string | null;
  // Selection props
  isSelected?: (id: string) => boolean;
  onToggleSelect?: (id: string) => void;
  onSelectAll?: (ids: string[]) => void;
  onDeselectAll?: () => void;
  isAllSelected?: boolean;
  isSomeSelected?: boolean;
  // Empty state
  emptyHint?: string;
  onAddKnowledge?: () => void;
}

const MyDocumentsSection = ({
  isLoading,
  documents,
  openDocumentDetails,
  handleDeleteDocument,
  handleSendToElevenLabs,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  headerRight,
  deletingDocumentId,
  isSelected,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  isAllSelected,
  isSomeSelected,
  emptyHint,
  onAddKnowledge,
}: MyDocumentsSectionProps) => {
  const { user } = useAuth();
  const selectionEnabled = !!onToggleSelect;
  const { documentParsingStatus, processingDocuments, retryingDocuments, retryParsing } =
    useDocumentParsingStatus(documents);

  const handleViewDocument = async (document: DocumentWithMetadata) => {
    if (document.source_path) {
      const signedUrl = await DocumentService.getSignedUrl(document.source_path);
      if (signedUrl) {
        window.open(signedUrl, "_blank");
      }
    }
  };

  const getParsingStatusIndicator = (document: DocumentWithMetadata) => {
    const isPdf = PDFService.isPdf(document);
    const status = documentParsingStatus[document.id];

    // For PDFs, use the full parsing status logic
    if (isPdf) {
      if (!status || status.isCompleted) {
        return null;
      }

      if (status.isProcessing) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-genie-secondary" />
                  <span className="text-xs text-genie-secondary">{status.progress}%</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>PDF is still being parsed. Please check back later.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return null;
    }

    // For non-PDF documents, show simple indicator based only on metadata.status
    const documentStatus = document.status || document.metadata?.status;
    if (
      documentStatus === "in_progress" ||
      documentStatus === "processing" ||
      documentStatus === "pending"
    ) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Loader2 className="h-4 w-4 text-genie-secondary animate-spin" />
                <span className="text-xs text-genie-secondary">Parsing...</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Document is being parsed. Please check back later.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return null;
  };

  const getDocumentStatusIcon = (document: DocumentWithMetadata) => {
    const docStatus = document.status || document.metadata?.status;
    const iconMap = {
      pending: { Icon: Loader2, color: "text-yellow-600", label: "Pending" },
      processing: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      in_progress: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      completed: { Icon: CheckCircle, color: "text-green-600", label: "Completed" },
      failed: { Icon: AlertCircle, color: "text-red-600", label: "Failed" },
    };
    const config = iconMap[docStatus || ""] || {
      Icon: AlertCircle,
      color: "text-gray-600",
      label: "Unknown",
    };
    return {
      Icon: config.Icon,
      color: config.color,
      label: config.label,
      isAnimated: docStatus === "processing" || docStatus === "in_progress",
    };
  };

  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl border border-genie-border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {selectionEnabled && documents.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAllSelected) {
                        onDeselectAll?.();
                      } else {
                        onSelectAll?.(documents.map((d) => d.id));
                      }
                    }}
                  >
                    <Checkbox
                      checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                      className="h-4.5 w-4.5"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isAllSelected ? "Deselect all" : "Select all"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="text-xs text-gray-500">
            {documents.length} {documents.length === 1 ? "document" : "documents"}
          </span>
        </div>
        {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-genie-primary animate-spin mr-2" />
          <span>Loading documents...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p className="text-sm font-medium text-gray-700">
            {emptyHint ?? "Your knowledge base is empty"}
          </p>
          {!emptyHint && (
            <p className="text-xs text-gray-500 mt-1">
              Upload a document, paste a URL, or import from cloud storage.
            </p>
          )}
          {onAddKnowledge && !emptyHint && (
            <Button
              onClick={onAddKnowledge}
              className="mt-5 bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-md shadow-genie-primary/20"
            >
              Add Knowledge
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid gap-1.5">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between px-3 py-2 rounded-lg border transition-all duration-200 cursor-pointer w-full overflow-hidden ${
                  doc.el_doc_id
                    ? "bg-genie-primary/5 border-genie-primary/20 hover:shadow-sm hover:border-genie-primary/30"
                    : "bg-white border-genie-border hover:shadow-sm hover:border-genie-secondary/30"
                }`}
                onClick={() => openDocumentDetails(doc)}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {selectionEnabled && (
                    <div
                      className="mr-2.5 shrink-0 flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelect?.(doc.id);
                      }}
                    >
                      <Checkbox
                        checked={isSelected?.(doc.id) ?? false}
                        className="h-4 w-4"
                      />
                    </div>
                  )}
                  <div className="mr-2.5 shrink-0">
                    <DocumentTypeIcon document={doc} iconSize="md" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                      <h3 className="font-medium text-sm text-genie-text truncate pr-1">
                        {doc.name}
                      </h3>
                      {(() => {
                        const { Icon, color, label, isAnimated } = getDocumentStatusIcon(doc);
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Icon
                                  className={`h-4 w-4 ${color} ${isAnimated ? "animate-spin" : ""}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{label}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      <span className="text-xs text-gray-400">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "N/A"}
                      </span>
                      {doc.el_doc_id ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-primary/10 text-genie-primary cursor-help">
                                <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                                In KB
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>In Knowledge Base</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-secondary/10 text-genie-secondary cursor-help">
                                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                                Not In KB
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Not in Knowledge Base</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {doc.user_id && user?.id && doc.user_id !== user.id && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-gray-700">
                          Team
                        </span>
                      )}

                      {(doc.owner?.full_name || doc.owner?.email) && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                          <UserRound className="h-2.5 w-2.5" />
                          <CopyableEmail email={doc.owner.full_name || doc.owner.email} />
                        </span>
                      )}

                      <FolderBadge folderId={doc.folder_id} size="sm" />
                    </div>
                    {doc.metadata?.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                        {doc.metadata.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-auto sm:hidden shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {doc.source_path && (
                          <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View source
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openDocumentDetails(doc)}>
                          View / edit details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {doc.source_type === "pdf_upload" &&
                          documentParsingStatus[doc.id]?.needsRetry &&
                          !documentParsingStatus[doc.id]?.isCompleted && (
                            <DropdownMenuItem onClick={() => retryParsing(doc)}>
                              Retry parsing
                            </DropdownMenuItem>
                          )}
                        {handleSendToElevenLabs &&
                          !doc.el_doc_id &&
                          (doc.optimized_path || doc.metadata?.optimized_file_path) && (
                            <DropdownMenuItem onClick={() => handleSendToElevenLabs(doc)}>
                              Add to Knowledge Base
                            </DropdownMenuItem>
                          )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteDocument(doc.id)}
                          disabled={deletingDocumentId === doc.id}
                          className="text-red-600"
                        >
                          {deletingDocumentId === doc.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Deleting...
                            </>
                          ) : (
                            "Delete"
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div
                  className="hidden sm:flex items-center gap-1.5 mt-2 sm:mt-0 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="ml-1">
                    <AgentAssociationDisplay
                      dependentAgents={doc.dependent_agents || []}
                      size="lg"
                      maxDisplay={5}
                      mode="avatars"
                      showLabel={false}
                    />
                  </div>
                  {getParsingStatusIndicator(doc)}
                  {doc.source_type === "pdf_upload" &&
                    documentParsingStatus[doc.id]?.needsRetry &&
                    !documentParsingStatus[doc.id]?.isCompleted && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-genie-secondary hover:text-genie-secondary/80 hover:bg-genie-secondary/10 border-genie-secondary/20"
                        onClick={() => retryParsing(doc)}
                        disabled={retryingDocuments.has(doc.id)}
                        title="Retry parsing this document"
                      >
                        {retryingDocuments.has(doc.id) ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            <span className="text-xs">Retrying</span>
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                            <span className="text-xs">Retry</span>
                          </>
                        )}
                      </Button>
                    )}

                  {handleSendToElevenLabs &&
                    !doc.el_doc_id &&
                    (doc.optimized_path || doc.metadata?.optimized_file_path) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-genie-secondary border-genie-secondary/30 bg-genie-secondary/5 hover:bg-genie-secondary/10 hover:border-genie-secondary/40 font-medium"
                              onClick={() => handleSendToElevenLabs(doc)}
                              disabled={
                                processingDocuments.has(doc.id) ||
                                (doc.source_type === "pdf_upload" &&
                                  documentParsingStatus[doc.id]?.isProcessing)
                              }
                            >
                              {processingDocuments.has(doc.id) ? (
                                <>
                                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                  Adding...
                                </>
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {doc.source_type === "pdf_upload" &&
                              documentParsingStatus[doc.id]?.isProcessing
                                ? "PDF is still being parsed"
                                : "Send to agent available Knowledge Base"}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  {doc.parsed_path && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-genie-primary hover:text-genie-primary-hover hover:bg-genie-primary-light"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View source file</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-genie-primary hover:bg-genie-primary-light"
                          onClick={() => openDocumentDetails(doc)}
                        >
                          <SquarePen className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View / edit details</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleDeleteDocument(doc.id)}
                          disabled={deletingDocumentId === doc.id}
                        >
                          {deletingDocumentId === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{deletingDocumentId === doc.id ? "Deleting..." : "Delete document"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button for Infinite Scroll */}
          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => fetchNextPage?.()}
                disabled={isFetchingNextPage}
                className="bg-white border-genie-border hover:bg-genie-primary-light hover:border-genie-primary"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyDocumentsSection;
